'use client';

import { useEffect, type ReactNode } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import { WorkspaceStoreProvider, WorkspaceYjsBridge } from './store';
import { WidgetCommandBusProvider } from '@/lib/workspaces/widget-command-bus-context';
import { WorkspaceAgentCommandReceiver } from './agent-command-receiver';
import { WebMcpToolsBridge } from './webmcp-tools-bridge';
import { WebMcpConsentDialog } from './webmcp-consent-dialog';
import { HostCaptureControl } from './host-capture-control';
import { CaptureGrantPrompt } from './capture-grant-prompt';
import { FullscreenNavProvider } from './fullscreen-nav';
import { WorkspaceDeepLinkConsumer } from './deep-link-consumer';
import { WorkspaceLinkInterceptor } from './link-interceptor';
import { WorkspaceCanvas } from './workspace-canvas';
import { LeftPanel } from './panels/left-panel';
import { CreateFab } from './create-fab';
import { SearchEverything } from './search-everything';
import { DockBar } from './dock-bar';
import { BrandCard } from './brand-card';
import { AnonBrandCard } from './anon-brand-card';
import { AnonAuthFab } from './anon-auth-fab';
import { ActivityFab } from './activity-fab';
import { NotificationsFab } from './notifications-fab';
import { GuestSaveBanner } from './guest-save-banner';
import { TopPanel } from './panels/top-panel';
import { CreateDialog } from './create-dialog';
import { PublishAppDialog } from './publish-app-dialog';
import { CreateWithDialog } from './create-with-dialog';
import {
  WorkspaceSettingsDialog,
} from './workspace-settings-dialog';
import { WidgetContentPicker } from '@/components/workspace/widget-content-picker';
import { WidgetAgentGrant } from '@/components/workspace/widget-agent-grant';
import { WidgetAgentEnsure } from '@/components/workspace/widget-agent-ensure';
import { WidgetAppEmail } from '@/components/workspace/widget-app-email';
import { WidgetInboxConsent } from '@/components/workspace/widget-inbox-consent';
import { WidgetSaveDialog } from '@/components/workspace/widget-save-dialog';
import { WidgetShareLauncher } from '@/components/workspace/widget-share-launcher';
import { ProjectDialogLauncher } from '@/components/casfs/project-launcher';
import { TransferTray } from '@/components/casfs/transfer-tray';
import { WidgetFilesAccessDialog } from '@/components/workspace/widget-files-access-dialog';
import { WidgetLocalFolderDialog } from '@/components/workspace/widget-local-folder-dialog';
import { LiquidGlassDefs } from './liquid-glass-defs';
import { PasteHandler } from './paste-handler';
import { WorkspaceCardActionsProvider } from './card-actions-context';
import { DiscoverHost } from './discover/discover-host';
import { MobileTabBar } from './mobile-tab-bar';
import { StageSheet } from './stage-sheet';
import { WorkspaceAnonProvider } from './anon-context';
import { SignupGateProvider } from './signup-gate';
import { StoreSignupGateBridge } from './store-signup-gate-bridge';
import { WorkspaceCanvasTwin } from './workspace-canvas-twin';
import { CanvasYjsProvider } from './canvas/yjs-mount';
import { presenceColorForUserId } from './presence/user-color';
import {
  type WorkspaceDTO,
  type WorkspaceItemDTO,
} from '@/lib/workspaces/types';
import type { WorkspaceContentCache } from './store';
import type { UserMenuUser } from '@/components/user-menu-dropdown';
import { cn } from '@/lib/utils';
import { useRailV2 } from '@/lib/workspaces/rail-flag';
import { WorkspaceRail } from './rail/workspace-rail';

/**
 * Stacking order (bottom → top):
 *   1. Canvas (React Flow + nodes + edges + vignette)
 *   2. Floating chrome (brand/search/action, dock, save indicator, FAB)
 *   3. Edge panels (closed handles + open glass cards)
 *
 * Pointer events are explicitly enabled per chrome element via
 * `pointer-events-auto` since the wrapping shell is `pointer-events-none`
 * over the canvas.
 */
export function WorkspaceShell({
  initialWorkspace,
  initialItems,
  initialContentMeta,
  currentUser,
  anonReadOnly = false,
  sourceWorkspaceId = null,
  initialOpenHref,
  initialPreview,
}: {
  initialWorkspace: WorkspaceDTO;
  initialItems: WorkspaceItemDTO[];
  /**
   * Pre-fetched content metadata keyed by `content.id`. Optional —
   * callers that haven't migrated yet (older tests, embedded views)
   * can omit this prop and the store falls back to per-node fetches.
   */
  initialContentMeta?: Record<string, WorkspaceContentCache>;
  /**
   * `null` switches the shell into anon-read-only mode: the brand
   * card is replaced with auth CTAs, the SSR Twin companion is
   * rendered alongside the canvas, and the signup gate is wired up
   * so any mutation opens `/signup`.
   */
  currentUser: UserMenuUser | null;
  /** Read-only flag for the anon entrypoint. Defaults to false. */
  anonReadOnly?: boolean;
  /**
   * Workspace id the anon visitor is browsing. Stamped into
   * `localStorage` on signup-gate open so the post-signup clone-from
   * endpoint can copy the right source. `null` outside the public
   * share / onboarding paths.
   */
  sourceWorkspaceId?: string | null;
  /**
   * Seed the dialog stack on first render — used by SSR shareable
   * routes (`/u`, `/p`, `/content`) to open a preview dialog before
   * client JS hydrates.
   */
  initialOpenHref?: string;
  /**
   * SSR-rendered preview body keyed by the dialog href it overrides.
   * When the active dialog matches `initialPreview.href`, the
   * registry's client-side body is replaced with this server-rendered
   * one so crawlers see the canonical content.
   */
  initialPreview?: { href: string; body: ReactNode; footer?: ReactNode };
}) {
  const railV2 = useRailV2();

  // iOS Safari: the browser's NATIVE page pinch-zoom must never fire on the
  // workspace surface. It ignores `user-scalable=no`, so the only reliable
  // blocks are preventDefault on Safari's proprietary gesture events plus
  // non-passive 2-finger touchmove. Without this, a pinch that misses our
  // canvas handler zooms the PAGE — and repeatedly page-zooming a canvas
  // full of live iframes blew up WebKit's renderer on real iPhones
  // (Safari's "A problem repeatedly occurred" crash loop). Scoped to this
  // shell's lifetime, so docs/marketing pages keep accessibility page-zoom;
  // in-app (iframe) touches never reach these apex listeners, so widgets'
  // own gestures are untouched.
  useEffect(() => {
    const preventGesture = (e: Event) => e.preventDefault();
    const preventPinch = (e: TouchEvent) => {
      if (e.touches.length > 1) e.preventDefault();
    };
    const opts = { passive: false } as AddEventListenerOptions;
    document.addEventListener('gesturestart', preventGesture, opts);
    document.addEventListener('gesturechange', preventGesture, opts);
    document.addEventListener('touchmove', preventPinch, opts);
    return () => {
      document.removeEventListener('gesturestart', preventGesture, opts);
      document.removeEventListener('gesturechange', preventGesture, opts);
      document.removeEventListener('touchmove', preventPinch, opts);
    };
  }, []);
  return (
    <WorkspaceStoreProvider
      initialWorkspace={initialWorkspace}
      initialItems={initialItems}
      initialContentMeta={initialContentMeta}
      anonReadOnly={anonReadOnly}
    >
      <WorkspaceAnonProvider
        anonReadOnly={anonReadOnly}
        sourceWorkspaceId={sourceWorkspaceId}
      >
      <WidgetCommandBusProvider workspaceId={initialWorkspace.id}>
      {/*
        Mounts the server→client RPC receiver for the
        `workspace.widgets.*` agent tools. Non-visual; lives inside
        the bus provider so it has access to the live bus instance.
        Filters incoming `agent.command` frames against the per-tab
        id so multi-tab workspaces don't double-execute.
      */}
      <WorkspaceAgentCommandReceiver workspaceId={initialWorkspace.id} />
      {/*
        WebMCP (ADR-0049): offers the open apps' declared commands to the
        browser's own assistant, through the same bus. Non-visual; dark
        unless `webmcpTools` is on in RuntimeConfig and the browser has the
        API. Not mounted on the `/w/` share surface (open point in the ADR).
      */}
      <WebMcpToolsBridge />
      {/*
        The rail's one-time consent (owner, 2026-10-01): the dialog the bridge
        opens the first time the browser's assistant calls a tool. Allow is
        remembered per person; withdrawn in Settings.
      */}
      <WebMcpConsentDialog />
      <HostCaptureControl />
      {/* Pixel-true screenshot handshake: an agent's capture request with no
          screen-share grant surfaces this one-time "share this tab" prompt. */}
      <CaptureGrantPrompt />
      <ReactFlowProvider>
        <FullscreenNavProvider
          initialOpenHref={initialOpenHref}
          initialPreview={initialPreview}
        >
        <SignupGateProvider>
        <StoreSignupGateBridge />
        <WorkspaceCardActionsProvider>
        {/*
          One-shot URL consumer for the workspace shell. Reads
          `?spawn`, `?preview`, `?dialog`, `?chat`, `?prompt` once
          on mount, dispatches into the right store/dialog action,
          then strips the params from the URL bar via
          history.replaceState. See `deep-link-consumer.tsx` for
          the full contract.
        */}
        <WorkspaceDeepLinkConsumer />
        {/*
          Intercept clicks on /u, /p, /content, /c links so they open in
          the FullscreenNav dialog instead of navigating away from the
          canvas. See `link-interceptor.tsx`.
        */}
        <WorkspaceLinkInterceptor />
        <div
          data-testid="workspace-shell"
          data-workspace-id={initialWorkspace.id}
          className={cn(
            'relative h-full w-full overflow-hidden',
            railV2 && 'flex',
          )}
        >
          {/* Hidden global SVG filter defs powering `.liquid-glass*`.
              Mounted at the root so any chrome can reference them via
              `backdrop-filter: url(#liquid-glass-*)` in CSS. Renders
              nothing visible itself. */}
          <LiquidGlassDefs />

          {/* Workspace rail (V2) — reserves a left column so the canvas
              becomes a clean rectangle. Behind WORKSPACE_RAIL_V2; when the
              flag is off the legacy floating chrome below renders
              unchanged. */}
          {railV2 && <WorkspaceRail user={currentUser} />}

          {/* Canvas column. `display:contents` in the legacy layout so the
              absolute chrome keeps positioning against the shell root
              exactly as before; a real flex child in V2 so the canvas and
              edge panels live to the right of the rail. */}
          <div
            className={
              railV2 ? 'relative flex-1 min-w-0 overflow-hidden' : 'contents'
            }
          >

          {/* Canvas -----------------------------------------------------
              Authenticated users mount inside <CanvasYjsProvider> so
              presence + canvas comments + multiplayer widgets have
              the workspace-scoped Y.Doc + Y.Awareness available via
              `useCanvasYjs()`. Anon visitors skip the provider —
              they have no cookie, the `collab/get-token` route would
              401, and the canvas is read-only for them anyway.
              Feature-flag (`COLLAB_V3_ENABLED`) is enforced server-
              side by the token route: when off, the provider's
              status flips to `unavailable` and consumer hooks fall
              back gracefully. */}
          {currentUser ? (
            <CanvasYjsProvider
              workspaceId={initialWorkspace.id}
              localUser={{
                userId: currentUser.id,
                username: currentUser.username,
                color: presenceColorForUserId(currentUser.id),
                avatarUrl: currentUser.avatarUrl ?? undefined,
              }}
            >
              {/* Wires the workspace Y.Doc into the store so the canvas is
                  CRDT-backed (items sync conflict-free + persist to
                  content_room_docs). Non-visual. */}
              <WorkspaceYjsBridge />
              <WorkspaceCanvas />
            </CanvasYjsProvider>
          ) : (
            <WorkspaceCanvas />
          )}

          {/*
            SSR Twin sibling — sr-only summary of the workspace
            (name, owner, each item's display title + body excerpt)
            that crawlers and LLM ingestion pick up since React-Flow
            itself is JS-only and invisible to them. Anon-only; the
            authed render skips it because Google doesn't index logged-
            in canvases anyway. See ADR 0017.
          */}
          {currentUser === null && (
            <WorkspaceCanvasTwin
              workspace={initialWorkspace}
              items={initialItems}
            />
          )}

          {/* Edge panels stay above all workspace chrome.
              Library (left) and Recent (right) surface workspace-
              specific user data — irrelevant to anon visitors who
              don't own this canvas, so we hide them. Bottom + Top
              panels stay (chats / notifications opener) — but the
              authed shell renders TopPanel via `NotificationsFab`,
              not directly, so anon never gets the top sheet either. */}
          <div className="absolute inset-0 pointer-events-none z-[90]">
            {/* Discover, opened: full width over the canvas area, rail still
                visible. Works for anon too — the surface has no panel events,
                and spawns mint a guest through the store fetch wrapper. */}
            {railV2 && <DiscoverHost />}
            {/* Mobile bottom bar: Discover · Screens · Alerts · You. Sits
                ABOVE the Discover overlay so switching stays one tap while
                browsing. Gated to coarse pointers below md inside. */}
            {/* Stage as a sheet below md (S1): covers the canvas while the
                overview's world lattice — unreadable at phone widths — runs
                harmlessly underneath. Below the bar, above Discover's close. */}
            {railV2 && <StageSheet />}
            {railV2 && <MobileTabBar user={currentUser} />}
            {currentUser && <LeftPanel />}
            {/* Bottom "Discover" panel removed: the marketplace is absorbed
                into the Apps/Agents views and the network feed into Feed.
                Only the Top (notifications) sheet remains an edge panel. */}
            <TopPanel />
          </div>

          {/* Guest reminder pill, centered over the canvas top. Only under
              the V2 rail — legacy chrome already owns the top row, and the
              rail has no persistent surface to carry the guest CTA. */}
          {railV2 && <GuestSaveBanner variant="floating" />}

          {/*
            Floating chrome -------------------------------------------
            One responsive top row: logo menu, search, notifications.
            Grid columns let the search shrink between the fixed left and
            right affordances without overlap. All three lanes use the
            same h-10 height so the bar reads as a single ribbon.

            Legacy chrome only — the V2 rail hosts brand / search /
            notifications / create. Gating the whole row off under railV2
            is what makes the canvas a clean rectangle with no top overlay.
          */}
          {!railV2 && (
          <div className="pointer-events-none absolute left-3 right-3 top-3 z-30 grid grid-cols-[auto_minmax(0,36rem)_auto] items-center gap-2 sm:gap-3">
            {/* Brand and notifications get explicit contextmenu
                suppression so right-clicking the round FAB / pill
                doesn't pop the browser's "Inspect / Save image"
                menu over our liquid-glass chrome. The search column
                deliberately doesn't get it — the input there needs
                the native copy / paste / spell-check menu. */}
            <div
              className="min-w-0"
              onContextMenu={(e) => e.preventDefault()}
            >
              {currentUser ? (
                <BrandCard user={currentUser} />
              ) : (
                <AnonBrandCard />
              )}
            </div>
            <div className="min-w-0">
              <SearchEverything />
            </div>
            <div
              className="flex justify-end"
              onContextMenu={(e) => e.preventDefault()}
            >
              {/* Notifications fab is meaningless for anon — it would
                  open a personal feed they can't have. We swap it for
                  the Log in / Sign up pills so the top-right slot
                  always carries the most important affordance for
                  the current auth state. Brand card on the left
                  stays identical (logo + wordmark) in both modes. */}
              {currentUser ? <NotificationsFab /> : <AnonAuthFab />}
            </div>
          </div>
          )}

          {/* Bottom offset adds `env(safe-area-inset-bottom)` (resolves to
              non-zero thanks to `viewportFit: 'cover'` on the canvas route)
              so the dock / FABs clear the Android system nav bar instead of
              hiding behind it. Legacy chrome only — the V2 rail replaces the
              dock and bottom FABs. */}
          {!railV2 && (
            <div
              className="ws-dock-wrap absolute bottom-[calc(0.75rem_+_env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-30"
              onContextMenu={(e) => e.preventDefault()}
            >
              <DockBar />
            </div>
          )}

          {/* `data-onboarding-keep`: tapping these bottom FABs must NOT
              dismiss the welcome overlay (see the selective-dismiss
              listener in WorkspaceOnboarding). Legacy chrome only — the V2
              rail hosts Create. */}
          {!railV2 && (
            <div
              className="absolute bottom-[calc(0.75rem_+_env(safe-area-inset-bottom))] left-3 z-30"
              onContextMenu={(e) => e.preventDefault()}
              data-onboarding-keep
            >
              <CreateFab />
            </div>
          )}

          {/* Activity (Marketplace shortcut) — bottom-right corner.
              Legacy chrome only — the V2 rail hosts Discover / Activity. */}
          {!railV2 && (
            <div
              className="absolute bottom-[calc(0.75rem_+_env(safe-area-inset-bottom))] right-3 z-30"
              onContextMenu={(e) => e.preventDefault()}
              data-onboarding-keep
            >
              <ActivityFab />
            </div>
          )}

          {/* Lazily-shown dialogs — render nothing until triggered. */}
          <CreateDialog />
          <PublishAppDialog />
          <CreateWithDialog />
          <WorkspaceSettingsDialog />
          <WidgetContentPicker />
          <WidgetAgentGrant />
          <WidgetAgentEnsure />
          <WidgetAppEmail />
          <WidgetInboxConsent />
          <WidgetSaveDialog />
          <WidgetShareLauncher />
          <ProjectDialogLauncher />
          <TransferTray />
          <WidgetFilesAccessDialog />
          <WidgetLocalFolderDialog />

          {/*
            First-visit onboarding now lives on the empty-workspace page
            (the Discover inline mount): new users land in an empty canvas that carries
            search + featured apps + a hello, so the old sketch overlay was
            retired. See docs/internal/architecture/onboarding-and-adaptive-workspaces.md.
          */}

          {/*
            Global paste handler — sticky annotation for text, content
            widget for image/video. Mounted as a non-visual sibling so
            it can `useReactFlow()` for flow-space coordinates without
            interfering with the canvas component itself.
          */}
          <PasteHandler />

          {/*
            Fullscreen widget takeover is implemented inside
            `NodeFrame` itself: when `fullscreenItemId === itemId`
            the frame layer is `createPortal`-ed to `document.body`
            with `fixed inset-0 z-[200]`. No overlay component is
            needed at this level — portaling escapes every chrome
            stacking context and the in-flow node retains its
            store-driven dimensions for the on-exit return trip.
          */}
          </div>
        </div>
        </WorkspaceCardActionsProvider>
        </SignupGateProvider>
        </FullscreenNavProvider>
      </ReactFlowProvider>
      </WidgetCommandBusProvider>
      </WorkspaceAnonProvider>
    </WorkspaceStoreProvider>
  );
}
